import React, { useState } from "react";
import {
  Flex,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";

import DataTable from "./DataTable";
import DataTableV2 from "../ui/data-table";
import PageTitle from "../PageTitle";
import { LoadingIndicator } from "../LoadingIndicator";
import { FLAGS, useBoolFlag } from "../../services/featureFlags";

const PageTableWithSearch = ({
  title,
  columns,
  data,
  onClickRow,
  initialSort,
  initialValue,
  tableId,
  // Extra controls (e.g. filters) shown beside the search box.
  filterControls,
  // While true the table area shows a spinner, but the header, search box and
  // any filterControls stay mounted, so changing a filter keeps the search.
  loading = false,
}) => {
  const [searchInput, setSearchInput] = useState(initialValue || "");

  // Only tables that opt in with a `tableId` can be switched to the new
  // table; everything else stays on the Chakra one.
  const responsiveTables = useBoolFlag(FLAGS.WEB_RESPONSIVE_TABLES);
  const useNewTable = Boolean(tableId) && responsiveTables;

  const searchBox = (
    <InputGroup maxW={filterControls ? { base: "full", md: "xs" } : "xs"}>
      <Input
        placeholder="Search"
        value={searchInput || ""}
        onChange={(e) => setSearchInput(e.target.value)}
        variant="black"
      />
      <InputRightElement pointerEvents="none">
        <Icon as={FiSearch} color="white" boxSize="5" />
      </InputRightElement>
    </InputGroup>
  );

  return (
    <Flex flex="1">
      {/* minW="0" is load-bearing, not cosmetic. A flex item defaults to
          min-width:auto, so without it this Stack grows to its content's
          width instead of its parent's. The responsive table measures its
          own container to decide which columns to collapse, so an inflated
          ancestor makes it read a width far wider than the viewport and
          conclude every column fits. That failure is content-dependent:
          with few short rows the table is narrower than the parent and it
          looks fine, which is why it survived local testing and only showed
          up on production data. */}
      <Stack spacing="1" flex="1" minW="0">
        <Stack
          direction={{
            base: "column",
            md: "row",
          }}
          justify="space-between"
          mb={8}
          mt={6}
        >
          <PageTitle title={title} />
          {filterControls ? (
            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={3}
              align={{ base: "stretch", md: "center" }}
            >
              {filterControls}
              {searchBox}
            </Stack>
          ) : (
            searchBox
          )}
        </Stack>

        {loading ? (
          <LoadingIndicator />
        ) : useNewTable ? (
          // minW="0" lets the table shrink inside the flex column so it can
          // scroll horizontally instead of forcing the page wide.
          <Flex minW="0" flex="1">
            <DataTableV2
              tableId={tableId}
              searchInput={searchInput}
              columns={columns}
              data={data}
              onClickRow={onClickRow}
              initialSort={initialSort}
            />
          </Flex>
        ) : (
          <Flex>
            <DataTable
              searchInput={searchInput}
              columns={columns}
              data={data}
              onClickRow={onClickRow}
              initialSort={initialSort}
            />
          </Flex>
        )}
      </Stack>
    </Flex>
  );
};

export default PageTableWithSearch;
